<script>
// Shared by every instance: one answer per chat for a minute, so hopping
// between chats (or remounting the panel) doesn't refetch. Refresh bypasses it.
const panelCache = new Map();
</script>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ConversationApi from 'dashboard/api/inbox/conversation';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import S3BukkuLinkDialog from './S3BukkuLinkDialog.vue';
import S3CardModal from './S3CardModal.vue';

// Smith3D customer data (cards, Bukku, orders…) rendered natively from the JR
// kanban app's JSON — see MhS3PanelsController for the contract. Read-only;
// strings are shown as text, links open in a new tab (Lark sign-in works there).
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const CACHE_MS = 60000;
// Long groups (big buyers' Bukku documents) show the newest few first.
const GROUP_PREVIEW = 3;

const TONE_CLASSES = {
  red: 'bg-n-ruby-3 text-n-ruby-11',
  amber: 'bg-n-amber-3 text-n-amber-11',
  green: 'bg-n-teal-3 text-n-teal-11',
  blue: 'bg-n-blue-3 text-n-blue-11',
  slate: 'bg-n-alpha-2 text-n-slate-11',
};

const { t } = useI18n();
const { isContactSidebarItemOpen, toggleSidebarUIState } = useUISettings();
// Stored as "closed" so the section starts open for everyone.
const isOpen = computed(() => !isContactSidebarItemOpen('is_s3_panel_closed'));

const data = ref(null);
const isLoading = ref(false);
const hasError = ref(false);
const showLinkDialog = ref(false);
// Board cards open in a modal on desktop; Ctrl/Cmd/Shift-click, other hosts
// (Bukku can't be framed) and small screens keep the normal new tab.
const CARD_HOST = 'kanban.smith3d.my';
const MODAL_MIN_WIDTH = '(min-width: 1024px)';
const cardModal = ref(null);
const isBoardCard = url => {
  try {
    return new URL(url).host === CARD_HOST;
  } catch {
    return false;
  }
};
const openItem = (event, item) => {
  const plainClick =
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey;
  if (
    !plainClick ||
    !isBoardCard(item.url) ||
    !window.matchMedia(MODAL_MIN_WIDTH).matches
  ) {
    return;
  }
  event.preventDefault();
  cardModal.value = { url: item.url, title: item.title };
};
const isUnlinking = ref(false);
// Smith3D decides when a section offers an action; unknown types are ignored.
const SECTION_ACTIONS = ['link_bukku', 'unlink_bukku'];
const isKnownAction = action => SECTION_ACTIONS.includes(action?.type);
const collapsed = ref({});
const expandedGroups = ref({});
const groupKey = (sectionIndex, groupIndex) => `${sectionIndex}-${groupIndex}`;
const visibleItems = (group, key) => {
  const items = group.items || [];
  return expandedGroups.value[key] ? items : items.slice(0, GROUP_PREVIEW);
};

const sections = computed(() => data.value?.sections || []);
const toneClass = tone => TONE_CLASSES[tone] || TONE_CLASSES.slate;
const isSafeUrl = url => typeof url === 'string' && url.startsWith('https://');
const itemCount = section =>
  (section.groups || []).reduce((sum, g) => sum + (g.items || []).length, 0);

// Link/unlink answer with the freshly built panel — show and cache it.
const applyPanel = payload => {
  panelCache.set(Number(props.conversationId), {
    data: payload,
    at: Date.now(),
  });
  data.value = payload;
};

const runAction = async action => {
  if (action.type === 'link_bukku') {
    showLinkDialog.value = true;
    return;
  }
  isUnlinking.value = true;
  try {
    const { data: payload } = await ConversationApi.unlinkBukkuContact(
      props.conversationId
    );
    applyPanel(payload);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('S3_CUSTOMER_PANEL.LINK.FAILED')
    );
  } finally {
    isUnlinking.value = false;
  }
};

const load = async (force = false) => {
  const id = Number(props.conversationId);
  const cached = panelCache.get(id);
  if (!force && cached && Date.now() - cached.at < CACHE_MS) {
    data.value = cached.data;
    hasError.value = false;
    return;
  }
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data: payload } = await ConversationApi.getS3Panel(id);
    panelCache.set(id, { data: payload, at: Date.now() });
    if (id === Number(props.conversationId)) data.value = payload;
  } catch {
    if (id === Number(props.conversationId)) hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => [props.conversationId, isOpen.value],
  ([, open]) => {
    data.value = null;
    collapsed.value = {};
    expandedGroups.value = {};
    if (open) load();
  },
  { immediate: true }
);
</script>

<template>
  <AccordionItem
    :title="t('S3_CUSTOMER_PANEL.TITLE')"
    :is-open="isOpen"
    @toggle="toggleSidebarUIState('is_s3_panel_closed')"
  >
    <template #button>
      <!-- span, not button: the accordion header is itself a <button> -->
      <span
        v-if="isOpen"
        v-tooltip.top="t('S3_CUSTOMER_PANEL.REFRESH')"
        role="button"
        tabindex="0"
        class="p-1 mr-2 rounded text-n-slate-11 hover:bg-n-alpha-2"
        @click.stop="!isLoading && load(true)"
        @keydown.enter.stop.prevent="!isLoading && load(true)"
      >
        <Icon
          icon="i-lucide-refresh-cw"
          class="size-3.5"
          :class="{ 'animate-spin': isLoading }"
        />
      </span>
    </template>

    <div class="flex flex-col gap-3">
      <p v-if="isLoading && !data" class="mb-0 text-sm text-n-slate-11">
        {{ t('S3_CUSTOMER_PANEL.LOADING') }}
      </p>
      <p v-else-if="hasError" class="mb-0 text-sm text-n-ruby-11">
        {{ t('S3_CUSTOMER_PANEL.UNAVAILABLE') }}
      </p>
      <p
        v-else-if="data && !sections.length"
        class="mb-0 text-sm text-n-slate-11"
      >
        {{ t('S3_CUSTOMER_PANEL.EMPTY') }}
      </p>

      <div
        v-for="(section, index) in sections"
        :key="index"
        class="overflow-hidden border rounded-lg border-n-weak"
      >
        <button
          type="button"
          class="flex items-center w-full gap-2 px-3 py-2 bg-n-alpha-1 text-start"
          @click="collapsed[index] = !collapsed[index]"
        >
          <Icon
            :icon="
              collapsed[index]
                ? 'i-lucide-chevron-right'
                : 'i-lucide-chevron-down'
            "
            class="size-3.5 text-n-slate-11 shrink-0"
          />
          <span class="flex-1 text-sm font-medium truncate text-n-slate-12">
            {{ section.title }}
          </span>
          <span v-if="section.summary" class="text-xs text-n-slate-11 shrink-0">
            {{ section.summary }}
          </span>
        </button>

        <div v-if="!collapsed[index]" class="flex flex-col gap-2 px-3 py-2">
          <p v-if="section.note" class="mb-0 text-xs text-n-slate-11">
            {{ section.note }}
          </p>
          <button
            v-if="isKnownAction(section.action)"
            type="button"
            class="self-start text-xs text-n-blue-11 hover:underline disabled:opacity-50"
            :disabled="isUnlinking"
            @click="runAction(section.action)"
          >
            {{ section.action.label }}
          </button>
          <p
            v-if="!itemCount(section) && section.empty"
            class="mb-0 text-sm text-n-slate-11"
          >
            {{ section.empty }}
          </p>
          <div
            v-for="(group, groupIndex) in section.groups || []"
            :key="groupIndex"
            class="flex flex-col gap-2"
          >
            <p
              v-if="group.title"
              class="pb-1 mb-0 text-xs font-medium border-b text-n-slate-12 border-n-weak"
            >
              {{ group.title }}
            </p>
            <div
              v-for="(item, itemIndex) in visibleItems(
                group,
                groupKey(index, groupIndex)
              )"
              :key="itemIndex"
              class="flex flex-col gap-0.5 min-w-0"
            >
              <div class="flex items-start gap-2 min-w-0">
                <span class="flex-1 min-w-0 text-sm text-n-slate-12">
                  <!-- Compact rows: the title is the link (opens in a new tab). -->
                  <a
                    v-if="isSafeUrl(item.url)"
                    v-tooltip.top="
                      item.url_label || t('S3_CUSTOMER_PANEL.OPEN')
                    "
                    :href="item.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="font-medium text-n-blue-11 hover:underline"
                    @click="openItem($event, item)"
                  >
                    {{ item.title }}
                    <Icon
                      icon="i-lucide-external-link"
                      class="inline-block align-[-1px] size-3"
                    />
                  </a>
                  <span v-else class="font-medium">{{ item.title }}</span>
                  <span v-if="item.tag" class="text-n-slate-11">
                    {{ ` · ${item.tag}` }}
                  </span>
                </span>
                <span
                  v-if="item.badge?.text"
                  class="px-1.5 py-0.5 text-xs rounded-md shrink-0"
                  :class="toneClass(item.badge.tone)"
                >
                  {{ item.badge.text }}
                </span>
              </div>
              <span v-if="item.subtitle" class="text-xs text-n-slate-11">
                {{ item.subtitle }}
              </span>
            </div>
            <button
              v-if="(group.items || []).length > GROUP_PREVIEW"
              type="button"
              class="text-xs text-start text-n-slate-11 hover:underline w-fit"
              @click="
                expandedGroups[groupKey(index, groupIndex)] =
                  !expandedGroups[groupKey(index, groupIndex)]
              "
            >
              {{
                expandedGroups[groupKey(index, groupIndex)]
                  ? t('S3_CUSTOMER_PANEL.SHOW_LESS')
                  : t('S3_CUSTOMER_PANEL.SHOW_MORE', {
                      count: group.items.length - GROUP_PREVIEW,
                    })
              }}
            </button>
          </div>
        </div>
      </div>

      <p v-if="data?.footnote" class="mb-0 text-xs text-n-slate-11">
        {{ data.footnote }}
      </p>
    </div>
    <S3CardModal
      v-if="cardModal"
      :conversation-id="conversationId"
      :url="cardModal.url"
      :title="cardModal.title"
      @close="
        updated => {
          cardModal = null;
          if (updated) load(true);
        }
      "
    />
    <S3BukkuLinkDialog
      v-if="showLinkDialog"
      :conversation-id="conversationId"
      @linked="applyPanel"
      @close="showLinkDialog = false"
    />
  </AccordionItem>
</template>
